import React, { useState, useEffect } from 'react';
import { PencilIcon, TrashIcon, TagIcon } from '@heroicons/react/24/outline';
import { DiseaseGroup } from '../../types/diseases';
import { diseaseGroupsApi } from '../../api/diseases';
import toast from 'react-hot-toast';

interface DiseaseGroupsListProps {
  onRefresh?: () => void;
}

const DiseaseGroupsList: React.FC<DiseaseGroupsListProps> = ({ onRefresh }) => {
  const [groups, setGroups] = useState<DiseaseGroup[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const groupsData = await diseaseGroupsApi.getAll();
      setGroups(groupsData);
    } catch (error) {
      console.error('Error loading disease groups:', error);
      toast.error('Failed to load disease groups');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (group: DiseaseGroup) => {
    if (window.confirm(`Are you sure you want to delete "${group.name}"?`)) {
      try {
        await diseaseGroupsApi.delete(group.id);
        await loadData();
        toast.success('Disease group deleted successfully');
        if (onRefresh) onRefresh();
      } catch (error) {
        console.error('Error deleting disease group:', error);
        toast.error('Failed to delete disease group');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="text-center py-12">
        <TagIcon className="mx-auto h-12 w-12 text-slate-400" />
        <h3 className="mt-2 text-sm font-semibold text-slate-900">No disease groups</h3>
        <p className="mt-1 text-sm text-slate-500">
          Get started by creating a new disease group.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
            Disease Groups ({groups.length})
          </h3>
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-slate-900 sm:pl-0"
                      >
                        Name
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900"
                      >
                        Description
                      </th>
                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-0"
                      >
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {groups.map((group) => (
                      <tr key={group.id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-slate-900 sm:pl-0">
                          {group.name}
                        </td>
                        <td className="px-3 py-4 text-sm text-slate-500 max-w-xs">
                          {group.description || '-'}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleDelete(group)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete disease group"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiseaseGroupsList; 